// FLEXX Trading Sessions Clock — auto-detects the trader's local TZ and
// surfaces which session is active right now (Tokyo / London / NY / Overlap),
// with a live countdown to the current session's close OR the next session's
// open. Drops in anywhere — Live Room banner, Journal banner, Daily Briefing,
// or future widgets. Zero props in the default form; pass `compact` for the
// pill-only variant used in tight headers.

import React, { useEffect, useState } from "react";
import {
  getSessionState, formatDuration, getUserTz, getUserTzAbbr,
} from "./flexxTime";

const T = {
  bg: "#0C0C0C",
  panel: "#111111",
  border: "#1C1C1C",
  gold: "#C9A227",
  goldGlow: "rgba(201,162,39,0.18)",
  text: "#EDE8DC",
  mute: "#AAAAAA",
};

export default function SessionsClock({ compact = false }) {
  const [state, setState] = useState(() => getSessionState());
  useEffect(() => {
    const tick = () => setState(getSessionState());
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, []);
  const { primary, active, upcoming, previous } = state;

  // Compact pill — used in tight headers (e.g. Daily Briefing inline).
  if (compact) {
    const pillColor = primary?.color || T.mute;
    const pillLabel = primary
      ? `${primary.name.toUpperCase()} · ${formatDuration(primary.msToEnd)} left`
      : `OFF-SESSION · ${upcoming ? formatDuration(upcoming.msToStart) : "—"} to ${upcoming?.name || "next"}`;
    return (
      <span data-testid="sessions-clock-pill" style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "4px 10px", borderRadius: 999,
        border: `1px solid ${pillColor}55`, background: `${pillColor}15`,
        color: pillColor, fontFamily: "monospace", fontSize: 10, letterSpacing: 1.2, fontWeight: 700,
      }}>
        <span data-testid="sessions-clock-pill-dot" style={{
          width: 6, height: 6, borderRadius: 3, background: pillColor,
          animation: primary ? "flexx-session-pulse 1.4s ease-in-out infinite" : "none",
        }} />
        {pillLabel}
      </span>
    );
  }

  // Full card — the headline UX.
  const userTz = getUserTz();
  const userTzAbbr = getUserTzAbbr();
  return (
    <section data-testid="sessions-clock" style={{
      background: `linear-gradient(135deg, ${T.bg} 0%, ${T.panel} 100%)`,
      border: `1px solid ${primary ? primary.color + "55" : T.border}`,
      borderRadius: 10, padding: 16, marginBottom: 18,
      boxShadow: primary ? `0 0 28px ${primary.color}25` : "none",
      transition: "border-color 0.4s, box-shadow 0.4s",
    }}>
      <style>{`
        @keyframes flexx-session-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.55; }
        }
      `}</style>

      {/* Top row — active session badge + countdown */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <ActiveBadge primary={primary} />
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 10, color: T.mute, fontFamily: "monospace", letterSpacing: 1.5 }}>
            {primary ? "CURRENT SESSION ENDS IN" : "NEXT SESSION OPENS IN"}
          </div>
          <div data-testid="sessions-clock-countdown" style={{
            fontFamily: "JetBrains Mono, monospace", fontWeight: 800, fontSize: 22,
            color: primary ? primary.color : T.gold, letterSpacing: 1, marginTop: 2,
          }}>
            {primary
              ? formatDuration(primary.msToEnd)
              : upcoming ? formatDuration(upcoming.msToStart) : "—"}
          </div>
        </div>
      </div>

      {/* Description row */}
      {(primary?.desc || upcoming?.desc) && (
        <div data-testid="sessions-clock-desc" style={{
          padding: "10px 12px", borderRadius: 8,
          background: `${primary?.color || T.gold}10`,
          borderLeft: `3px solid ${primary?.color || T.gold}`,
          color: T.text, fontSize: 12, lineHeight: 1.5, marginBottom: 14,
          fontStyle: "italic",
        }}>
          {primary
            ? primary.desc
            : `Off-session — markets are quiet. ${upcoming?.name || "Next session"} opens at ${upcoming?.startLocal || "—"} (${userTzAbbr}). Use this time to journal or review.`}
        </div>
      )}

      {/* 3-session timeline */}
      <SessionTimeline active={active} primary={primary} />

      {/* Footer — previous + next */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.border}`,
        fontSize: 10, fontFamily: "monospace", color: T.mute, letterSpacing: 1, gap: 12, flexWrap: "wrap",
      }}>
        <span data-testid="sessions-clock-previous">
          {previous ? `← ${previous.short} CLOSED ${previous.endLocal} ${userTzAbbr}` : ""}
        </span>
        <span data-testid="sessions-clock-tz" style={{ color: T.mute }}>
          🌐 {userTz} · {userTzAbbr}
        </span>
        <span data-testid="sessions-clock-upcoming">
          {upcoming ? `${upcoming.short} OPENS ${upcoming.startLocal} ${userTzAbbr} →` : ""}
        </span>
      </div>
    </section>
  );
}

function ActiveBadge({ primary }) {
  if (!primary) {
    return (
      <div data-testid="sessions-clock-badge" style={{
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <div style={{
          width: 12, height: 12, borderRadius: 6, background: T.mute, opacity: 0.6,
        }} />
        <div>
          <div style={{ fontSize: 10, color: T.mute, fontFamily: "monospace", letterSpacing: 2 }}>NOW</div>
          <div data-testid="sessions-clock-active-name" style={{ color: T.text, fontWeight: 800, fontSize: 18, fontFamily: "'Bebas Neue','Oswald',sans-serif", letterSpacing: 1 }}>
            OFF-SESSION
          </div>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="sessions-clock-badge" style={{
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <div style={{
        width: 12, height: 12, borderRadius: 6, background: primary.color,
        animation: "flexx-session-pulse 1.4s ease-in-out infinite",
        boxShadow: `0 0 12px ${primary.color}`,
      }} />
      <div>
        <div style={{ fontSize: 10, color: primary.color, fontFamily: "monospace", letterSpacing: 2 }}>NOW · ACTIVE</div>
        <div data-testid="sessions-clock-active-name" style={{
          color: T.text, fontWeight: 800, fontSize: 22,
          fontFamily: "'Bebas Neue','Oswald',sans-serif", letterSpacing: 1,
        }}>
          {primary.name.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

function SessionTimeline({ active, primary }) {
  const isOverlap = primary?.id === "overlap-ldn-ny";
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
      {[
        { id: "tokyo",   name: "Tokyo",    short: "TKO", color: "#B57BFF" },
        { id: "london",  name: "London",   short: "LDN", color: "#4A90E2" },
        { id: "newyork", name: "New York", short: "NY",  color: "#FF6B35" },
      ].map((s) => {
        const live = active.find((a) => a.id === s.id);
        const isLive = !!live;
        return (
          <div key={s.id} data-testid={`sessions-clock-card-${s.id}`} style={{
            padding: 10, borderRadius: 8,
            background: isLive ? `${s.color}18` : "#0a0a0a",
            border: `1px solid ${isLive ? s.color : T.border}`,
            borderLeft: `3px solid ${isLive ? s.color : T.border}`,
            transition: "all 0.4s ease",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginBottom: 4 }}>
              <span style={{
                color: isLive ? s.color : T.mute, fontFamily: "monospace",
                fontSize: 9, letterSpacing: 2, fontWeight: 700,
              }}>{s.short}</span>
              {isLive && (
                <span data-testid={`sessions-clock-card-${s.id}-live`} style={{
                  fontSize: 8, padding: "1px 6px", borderRadius: 3,
                  background: s.color, color: "#0a0a0a", fontWeight: 800,
                  fontFamily: "monospace", letterSpacing: 1,
                  animation: "flexx-session-pulse 1.4s ease-in-out infinite",
                }}>{isOverlap ? "🔥" : "LIVE"}</span>
              )}
            </div>
            <div style={{ color: isLive ? T.text : T.mute, fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              {s.name}
            </div>
            <div style={{ color: T.mute, fontSize: 10, fontFamily: "monospace" }}>
              {live ? `${live.startLocal} – ${live.endLocal}` : ""}
            </div>
            {/* Progress bar for active session */}
            {isLive && live.progress != null && (
              <div data-testid={`sessions-clock-card-${s.id}-progress`} style={{
                height: 3, background: `${s.color}33`, borderRadius: 2,
                marginTop: 6, overflow: "hidden", position: "relative",
              }}>
                <div style={{
                  width: `${Math.round(live.progress * 100)}%`, height: "100%",
                  background: s.color, transition: "width 1s linear",
                }} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
